import { memoize } from "es-toolkit";

import { type GetNodeSlotId } from "@skilitics/nautilus-core-designer-data";

import type { GetManifestByTypeId } from "../types/index.ts";
import type { GetMonolingualDesignerNodeData } from "./contextualSelectors/index.ts";
import { withProfileChildNodes, type GetNodeProfileById } from "./nodePublicApi.ts";

type ContextUnknown = Record<string, unknown>;

// Important: keep a stable empty object reference for memoization.
const EMPTY_CONTEXT: ContextUnknown = {};

export const getParentContextByIdCreate = (
  getManifest: GetManifestByTypeId,
  getMonolingualDesignerNodeData: GetMonolingualDesignerNodeData,
  getSlotId: GetNodeSlotId,
  rootNodeParentContext: ContextUnknown,
  getNodeProfileById: GetNodeProfileById,
) => {
  const getNodeParentContext: GetParentContextById = memoize((id) => {
    const slotId = getSlotId(id);
    if (!slotId) {
      return rootNodeParentContext;
    }
    const { parentId, name } = slotId;
    const parent = getMonolingualDesignerNodeData(parentId);
    const {
      designer: { getChildContexts },
    } = getManifest(parent.typeId);
    const childContexts =
      getChildContexts?.(
        withProfileChildNodes(parent, (child) => getNodeProfileById(child.id)),
        getNodeParentContext(parentId),
      ) ?? EMPTY_CONTEXT;
    const childContext = childContexts[name] ?? EMPTY_CONTEXT;
    if (typeof childContext === "function") {
      return (childContext as (id: number) => ContextUnknown)(id);
    }
    return childContext as ContextUnknown;
  });

  return getNodeParentContext;
};

export type GetParentContextById = (id: number) => ContextUnknown;
